/**
 * Utilities for generating "Add to Calendar" links/files from appointment data.
 */

function parseAppointmentDateTime(appointment) {
  const [month, day, year] = (appointment.date || "").split("/").map(Number);

  let timeStr = appointment.time || "";
  if (timeStr.includes(" - ")) {
    timeStr = timeStr.split(" - ")[0];
  }

  const timeParts = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!timeParts) {
    return { startDate: new Date(year, month - 1, day, 9, 0), durationHours: 1 };
  }

  let hours = parseInt(timeParts[1], 10);
  const minutes = parseInt(timeParts[2], 10);
  const meridiem = timeParts[3].toUpperCase();

  if (meridiem === "PM" && hours !== 12) hours += 12;
  if (meridiem === "AM" && hours === 12) hours = 0;

  const durationHours = appointment.serviceHours || appointment.hours || 1;
  return { startDate: new Date(year, month - 1, day, hours, minutes), durationHours };
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function formatLocalDateTime(date) {
  return (
    date.getFullYear() +
    pad(date.getMonth() + 1) +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    pad(date.getMinutes()) +
    pad(date.getSeconds())
  );
}

function buildLocation(appointment) {
  const addr = appointment?.shopData?.shopContact?.address;
  if (!addr) return "";
  return [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(", ");
}

function buildTitle(appointment) {
  const shopName = appointment?.shopData?.shopName?.name || "";
  const service = appointment?.service || "Appointment";
  return shopName ? `${service} at ${shopName}` : service;
}

/**
 * Opens a new tab/window with the Google Calendar "create event" URL.
 */
export function openGoogleCalendar(appointment) {
  const { startDate, durationHours } = parseAppointmentDateTime(appointment);
  const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: buildTitle(appointment),
    dates: `${formatLocalDateTime(startDate)}/${formatLocalDateTime(endDate)}`,
    details: `Scheduled via online booking`,
    location: buildLocation(appointment),
  });

  window.open(
    `https://calendar.google.com/calendar/render?${params.toString()}`,
    "_blank",
    "noopener"
  );
}

/**
 * Generates and downloads an .ics file (opens in Apple Calendar on Apple devices).
 */
export function downloadIcsFile(appointment) {
  const { startDate, durationHours } = parseAppointmentDateTime(appointment);
  const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);

  const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@oktorocket`;
  const now = formatLocalDateTime(new Date());

  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//OktoRocket//Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${formatLocalDateTime(startDate)}`,
    `DTEND:${formatLocalDateTime(endDate)}`,
    `SUMMARY:${buildTitle(appointment)}`,
    `LOCATION:${buildLocation(appointment)}`,
    `DESCRIPTION:Scheduled via online booking`,
    "STATUS:CONFIRMED",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");

  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = "appointment.ics";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
